/**
 * Iter 67ch — Admin: FAQ items.
 *
 * Curate the homepage FAQ accordion. Position controls order, is_active hides
 * an item without deleting it. Drag-handle is intentionally absent — vendors
 * just nudge the position number which is simpler and IDE-friendly.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { HelpCircle, Plus, Pencil, Trash2, Loader2, Save } from "lucide-react";

const _newDraft = () => ({ question: "", answer: "", position: 0, is_active: true });

export default function AdminFaqPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(_newDraft());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/admin/faq")).data || []); }
    catch (err) { toast.error(formatApiError(err) || "Could not load FAQ."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCreate = () => { setEditing(null); setDraft({ ..._newDraft(), position: rows.length }); setDialogOpen(true); };
  const onEdit = (r) => { setEditing(r); setDraft({ question: r.question, answer: r.answer, position: r.position ?? 0, is_active: r.is_active !== false }); setDialogOpen(true); };
  const onSave = async () => {
    if (!draft.question.trim() || !draft.answer.trim()) { toast.error("Question and answer required."); return; }
    setSaving(true);
    try {
      const body = {
        question: draft.question.trim(),
        answer: draft.answer.trim(),
        position: Number(draft.position || 0),
        is_active: !!draft.is_active,
      };
      if (editing) await api.patch(`/admin/faq/${editing.id}`, body);
      else await api.post("/admin/faq", body);
      toast.success(editing ? "Updated." : "Added.");
      setDialogOpen(false); load();
    } catch (err) { toast.error(formatApiError(err) || "Save failed."); }
    finally { setSaving(false); }
  };
  const onDelete = async (r) => {
    if (!window.confirm(`Delete "${r.question}"?`)) return;
    try { await api.delete(`/admin/faq/${r.id}`); toast.success("Deleted."); load(); }
    catch (err) { toast.error(formatApiError(err) || "Delete failed."); }
  };
  const onToggle = async (r) => {
    try { await api.patch(`/admin/faq/${r.id}`, { is_active: !r.is_active }); load(); }
    catch (err) { toast.error(formatApiError(err) || "Toggle failed."); }
  };

  return (
    <div className="space-y-4" data-testid="admin-faq-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-lg font-medium flex items-center gap-2"><HelpCircle className="h-5 w-5 text-indigo-600" /> Frequently Asked Questions</h3>
          <p className="text-xs text-neutral-500">Shown on the homepage accordion. The JSON-LD FAQPage schema is regenerated automatically.</p>
        </div>
        <Button onClick={onCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="faq-add">
          <Plus className="h-4 w-4 mr-1.5" /> Add question
        </Button>
      </div>

      <div className="rounded-2xl border border-border bg-white overflow-hidden">
        {loading && <div className="py-10 text-center text-neutral-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="py-10 text-center text-sm text-neutral-500">No FAQs yet. Add your first question above.</div>
        )}
        {!loading && rows.map((r) => (
          <div key={r.id} className="px-5 py-4 border-b border-border last:border-0 flex items-start gap-4" data-testid={`faq-row-${r.id}`}>
            <div className="flex-shrink-0">
              <Badge variant="outline" className="text-[10px] tabular-nums">#{r.position ?? 0}</Badge>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{r.question}</div>
              <div className="text-xs text-neutral-600 mt-1 line-clamp-2">{r.answer}</div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Switch checked={!!r.is_active} onCheckedChange={() => onToggle(r)} className="data-[state=checked]:bg-emerald-600" data-testid={`faq-toggle-${r.id}`} />
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(r)} data-testid={`faq-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => onDelete(r)} data-testid={`faq-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => !saving && setDialogOpen(o)}>
        <DialogContent className="sm:max-w-lg" data-testid="faq-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit question" : "New question"}</DialogTitle>
            <DialogDescription>Keep questions short and conversational — answers can be 1–3 sentences.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Question</Label>
              <Input value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} className="mt-1" data-testid="faq-dialog-question" />
            </div>
            <div>
              <Label>Answer</Label>
              <Textarea value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })} rows={4} className="mt-1" data-testid="faq-dialog-answer" />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label>Position</Label>
                <Input type="number" step="1" min="0" value={draft.position} onChange={(e) => setDraft({ ...draft, position: e.target.value })} className="mt-1 tabular-nums" data-testid="faq-dialog-position" />
              </div>
              <label className="flex items-center gap-2 text-sm pt-5">
                <Switch checked={!!draft.is_active} onCheckedChange={(v) => setDraft({ ...draft, is_active: !!v })} className="data-[state=checked]:bg-emerald-600" />
                Active
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={onSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="faq-dialog-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
              {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
